import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/utils/cn';

export function Hero() {
  const [isLoaded, setIsLoaded] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <section 
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #567D38 0%, #1a3d2a 100%)' }}
    >
      {/* Background Image Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(/images/hero-bg.jpg)',
          opacity: 0.2 
        }} 
        aria-hidden="true"
      />
      
      {/* Gradient Overlay */}
      <div 
        className="absolute inset-0"
        style={{ 
          background: 'linear-gradient(135deg, rgba(38, 93, 62, 0.95) 0%, rgba(38, 93, 62, 0.85) 50%, rgba(26, 43, 31, 0.9) 100%)' 
        }} 
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 container-marple pt-32 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Hero photo */}
          <div
            className={cn(
              'mx-auto mb-8 w-full max-w-3xl rounded-2xl overflow-hidden transition-all duration-700 shadow-xl',
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
            style={{ aspectRatio: '4/3' }}
          >
            <img
              src="/hero/hero-photo.jpg"
              alt="Two smiling Marple & Co customers with radiant skin"
              className="w-full h-full object-cover"
              style={{ objectPosition: '50% 8%' }}
              width={1066}
              height={1600}
            />
          </div>

          {/* Main Heading */}
          <h1
            className={cn(
              'font-serif normal-case text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight mb-8 transition-all duration-700',
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <em className="not-italic" style={{ 
              color: '#c5e47a',
              fontStyle: 'italic',
              position: 'relative'
            }}>
              Root-to-Radiance
              <span 
                className="absolute bottom-2 left-0 w-full h-1 rounded-sm"
                style={{ 
                  background: 'linear-gradient(90deg, #BDD630, #F4C95D)',
                  opacity: 0.6 
                }} 
              />
            </em>
            <br />
            <span className="text-white">African Holistic Skincare</span>
          </h1>

          {/* Subtitle */}
          <p 
            className={cn(
              'text-xl text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed transition-all duration-700 delay-100',
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            From the inside out — using powerful African botanicals + modern actives. 
            Topical. Ingestible. Supported.
          </p>

          {/* CTA Button */}
          <div 
            className={cn(
              'transition-all duration-700 delay-200',
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <Link
              to="/skin-analysis"
              className="inline-flex items-center gap-3 px-8 py-4 text-lg font-semibold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-white/50"
              style={{ 
                background: '#BDD630',
                color: '#567D38'
              }}
            >
              Reveal Your Skin's Story — Free
              <ArrowRight className="w-5 h-5" aria-hidden="true" />
            </Link>
          </div>

          {/* Trust Badges */}
          <div 
            className={cn(
              'flex flex-wrap justify-center gap-6 mt-12 text-white/80 text-sm transition-all duration-700 delay-300',
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <span className="flex items-center gap-2">
              <span className="text-[#BDD630]" aria-hidden="true">✓</span> Dermatologically Tested
            </span>
            <span className="flex items-center gap-2">
              <span className="text-[#BDD630]" aria-hidden="true">✓</span> Family-owned
            </span>
            <span className="flex items-center gap-2">
              <span className="text-[#BDD630]" aria-hidden="true">✓</span> Real results for sensitive skin
            </span>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2" aria-hidden="true">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
}

export default Hero;
